import { useCallback } from 'react'
import { atomFamily, selectorFamily, useRecoilValue, useSetRecoilState } from 'recoil'

export const progressState = atomFamily<number, string>({
    key: 'windowLoading/progress',
    default: 0,
})

const totalState = atomFamily<number, string>({
    key: 'windowLoading/total',
    default: 0,
})

const errorState = atomFamily<string | undefined, string>({
    key: 'windowLoading/error',
    default: undefined,
})

const noDataState = atomFamily<boolean, string>({
    key: 'windowLoading/noData',
    default: false,
})

const messageState = selectorFamily<string, string>({
    key: 'windowLoading/message',
    get: (id) => ({ get }) => {
        const progress = get(progressState(id))
        const total = get(totalState(id))
        const error = get(errorState(id))
        const noData = get(noDataState(id))

        if (error) return "Server Error"
        if (noData) return "No data"
        if (total > 0 && progress === total) return "Ready"
        if (total > 0) return `Loading (${progress}/${total})`
        return ""
    },
})

export function useUpdateWindowProgress(id: string) {
    const setProgress = useSetRecoilState(progressState(id))
    const setTotal = useSetRecoilState(totalState(id))
    const setError = useSetRecoilState(errorState(id))
    const setNoData = useSetRecoilState(noDataState(id))

    const updateProgress = useCallback((progress: number, total: number, error: string | undefined = undefined, noData = false) => {
        setProgress(progress)
        setTotal(total)
        setError(error)
        setNoData(noData)
    }, [setError, setNoData, setProgress, setTotal])

    return { updateProgress }
}

export function useWindowLoadingState(id: string) {
    const progress = useRecoilValue(progressState(id))
    const total = useRecoilValue(totalState(id))
    const message = useRecoilValue(messageState(id))
    const error = useRecoilValue(errorState(id))
    const noData = useRecoilValue(noDataState(id))

    return { message, progress, total, error, noData }
}
